/* Phase 59b — Cross-Strategy Portfolio View.
   Aggregates paper P&L from Momentum, ORB, Gap-and-Go, and VWAP+OI so users
   see their whole hypothetical book on one screen. */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, PieChart, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STRATEGY_ACCENT = {
  Momentum:      "var(--bull)",
  ORB:           "#7ec8ff",
  "Gap-and-Go":  "#ffb66e",
  "VWAP+OI":     "#c58bff",
};

const KpiCard = ({ label, value, tone = "muted", testid, sub }) => (
  <div className="border border-grid p-3 flex flex-col gap-1" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data">{label}</div>
    <div className="text-2xl font-mono-data" style={{ color: `var(--${tone})` }}>{value ?? "—"}</div>
    {sub && <div className="text-[10px] text-muted-3 font-mono-data">{sub}</div>}
  </div>
);

const PeriodPicker = ({ period, setPeriod, testid }) => (
  <div className="flex items-center gap-1">
    {["today", "week", "month"].map(p => (
      <button key={p} onClick={() => setPeriod(p)}
              data-testid={`${testid}-${p}`}
              className="px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
              style={{ borderColor: period === p ? "var(--bull)" : "var(--grid)",
                        color: period === p ? "var(--bull)" : "var(--text-muted-2)" }}>
        {p}
      </button>
    ))}
  </div>
);

const PortfolioPage = () => {
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/portfolio/summary`, { params: { period } }),
        axios.get(`${API}/portfolio/trades`, { params: { period, status: statusFilter || undefined, limit: 500 } }),
      ]);
      setSummary(s.data);
      setTrades(t.data.trades || []);
    } catch (e) {
      toast.error("Portfolio load failed");
    } finally { setLoading(false); }
  }, [period, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const totals = summary?.totals || {};
  const byStrat = summary?.by_strategy || [];
  // Compute max absolute strategy P&L for the bar-scaling
  const maxAbs = Math.max(1, ...byStrat.map(s => Math.abs(s.total_pnl || 0)));

  return (
    <section className="pt-6 pb-16 px-6 md:px-10 xl:px-14" data-testid="portfolio-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-3 font-mono-data flex items-center gap-2">
        <Briefcase size={12} style={{ color: "var(--bull)" }} /> Cross-Strategy Paper Book
      </div>
      <h1 className="mt-1 font-heading text-4xl sm:text-5xl lg:text-6xl">Portfolio</h1>
      <p className="mt-2 text-sm text-muted-2 max-w-3xl">
        Combined paper-trade P&amp;L across <strong>Momentum</strong> ·
        <strong> ORB</strong> · <strong>Gap-and-Go</strong> ·
        <strong> VWAP+OI</strong> — everything the platform is trading
        hypothetically on your behalf, on one screen.
      </p>

      <div className="mt-6 flex items-center justify-between flex-wrap gap-2">
        <PeriodPicker period={period} setPeriod={setPeriod} testid="portfolio-period" />
        <button onClick={load} disabled={loading} data-testid="portfolio-refresh"
                className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                style={{ borderColor: "var(--grid)", color: "var(--text-muted-2)" }}>
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Aggregate KPIs */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total Trades" value={totals.total_trades ?? 0} tone="text" testid="pf-kpi-trades" />
        <KpiCard label="Win Rate" value={`${totals.win_rate_pct ?? 0}%`} tone="text" testid="pf-kpi-winrate" />
        <KpiCard label="Total P&L (₹)"
                  value={totals.total_pnl >= 0 ? `+${totals.total_pnl}` : totals.total_pnl}
                  tone={totals.total_pnl >= 0 ? "bull" : "bear"} testid="pf-kpi-pnl" />
        <KpiCard label="Wins / Losses" value={`${totals.wins ?? 0} / ${totals.losses ?? 0}`} tone="text" testid="pf-kpi-wl" />
        <KpiCard label="Open Trades" value={totals.open_trades ?? 0} tone="bull" testid="pf-kpi-open" />
      </div>

      {/* Per-strategy breakdown */}
      <div className="mt-6 border border-grid p-3">
        <div className="flex items-center gap-2 mb-3 font-mono-data text-[11px]">
          <PieChart size={12} style={{ color: "var(--bull)" }} />
          <span className="uppercase tracking-widest text-muted-2">By Strategy</span>
        </div>
        <div className="space-y-2" data-testid="pf-by-strategy">
          {byStrat.map(s => {
            const pct = Math.abs(s.total_pnl || 0) / maxAbs * 100;
            const positive = (s.total_pnl || 0) >= 0;
            return (
              <div key={s.strategy} data-testid={`pf-row-${s.strategy}`}
                   className="border-b border-grid pb-2 font-mono-data text-[11px]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-3 rounded-sm"
                          style={{ background: STRATEGY_ACCENT[s.strategy] || "var(--bull)" }} />
                    <span style={{ color: STRATEGY_ACCENT[s.strategy] }}>{s.strategy}</span>
                    <span className="text-muted-3 text-[10px]">
                      {s.total_trades}t · {s.wins}W / {s.losses}L · {s.win_rate_pct}% · {s.open_trades} open
                    </span>
                  </div>
                  <span style={{ color: positive ? "var(--bull)" : "var(--bear)" }}>
                    {positive ? "+" : ""}{s.total_pnl}
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/[0.03] overflow-hidden">
                  {(s.total_pnl || 0) === 0 ? (
                    <div className="h-full opacity-40"
                         style={{ background: `repeating-linear-gradient(90deg, var(--grid) 0 3px, transparent 3px 8px)` }} />
                  ) : (
                    <div className="h-full" style={{
                      width: `${pct}%`,
                      background: positive ? "var(--bull)" : "var(--bear)",
                      opacity: 0.6,
                    }} />
                  )}
                </div>
              </div>
            );
          })}
          {!byStrat.length && (
            <div className="text-[11px] text-muted-3 font-mono-data py-4 text-center">
              No paper trades in this window yet.
            </div>
          )}
        </div>
      </div>

      {/* Daily P&L */}
      {summary?.daily_pnl?.length ? (
        <div className="mt-6 border border-grid p-3">
          <div className="text-[9px] uppercase tracking-widest text-muted-3 font-mono-data mb-2">
            Daily P&L (all strategies combined)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 font-mono-data text-[11px]">
            {summary.daily_pnl.map(d => (
              <div key={d.day} className="flex justify-between px-1">
                <span className="text-muted-2">{d.day}</span>
                <span style={{ color: d.pnl_abs >= 0 ? "var(--bull)" : "var(--bear)" }}>
                  {d.pnl_abs >= 0 ? "+" : ""}{d.pnl_abs}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Merged trades table */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[10px] text-muted-3 font-mono-data uppercase tracking-widest">
            All Paper Trades ({trades.length})
          </div>
          <div className="flex items-center gap-1">
            {["", "OPEN", "CLOSED"].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)}
                      data-testid={`pf-status-${f || "all"}`}
                      className="px-2 py-1 border text-[10px] uppercase font-mono-data hover:bg-white/[0.03]"
                      style={{ borderColor: statusFilter === f ? "var(--bull)" : "var(--grid)",
                                color: statusFilter === f ? "var(--bull)" : "var(--text-muted-2)" }}>
                {f || "All"}
              </button>
            ))}
          </div>
        </div>
        <div className="border border-grid overflow-x-auto">
          <table className="w-full font-mono-data text-[11px]" data-testid="pf-trades-table">
            <thead>
              <tr className="text-muted-3 uppercase tracking-widest text-[9px]">
                <th className="text-left px-2 py-1.5 border-b border-grid">Entry</th>
                <th className="text-left px-2 py-1.5 border-b border-grid">Strategy</th>
                <th className="text-left px-2 py-1.5 border-b border-grid">Symbol</th>
                <th className="text-center px-2 py-1.5 border-b border-grid">Dir</th>
                <th className="text-right px-2 py-1.5 border-b border-grid">Entry ₹</th>
                <th className="text-right px-2 py-1.5 border-b border-grid">Exit ₹</th>
                <th className="text-right px-2 py-1.5 border-b border-grid">P&L ₹</th>
                <th className="text-right px-2 py-1.5 border-b border-grid">P&L %</th>
                <th className="text-center px-2 py-1.5 border-b border-grid">Status</th>
                <th className="text-center px-2 py-1.5 border-b border-grid">Exit</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => (
                <tr key={i} className="hover:bg-white/[0.02]">
                  <td className="px-2 py-1 text-muted-2">
                    {(t.entry_ts || t.entered_at || "").slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="px-2 py-1" style={{ color: STRATEGY_ACCENT[t._strategy] || "var(--text-muted-2)" }}>
                    {t._strategy}
                  </td>
                  <td className="px-2 py-1" style={{ color: "var(--bull)" }}>{t.symbol}</td>
                  <td className="px-2 py-1 text-center"
                       style={{ color: (t.direction || t.side) === "LONG" || t.direction === "L" ? "var(--bull)" : "var(--bear)" }}>
                    {(t.direction || t.side || "L").toString().charAt(0)}
                  </td>
                  <td className="px-2 py-1 text-right">{t.entry_price ?? "—"}</td>
                  <td className="px-2 py-1 text-right">{t.exit_price ?? t.last_ltp ?? "—"}</td>
                  <td className="px-2 py-1 text-right"
                       style={{ color: (t.pnl_abs || 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                    {t.pnl_abs != null ? ((t.pnl_abs >= 0 ? "+" : "") + t.pnl_abs) : "—"}
                  </td>
                  <td className="px-2 py-1 text-right"
                       style={{ color: (t.pnl_pct || 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                    {t.pnl_pct != null ? `${t.pnl_pct >= 0 ? "+" : ""}${t.pnl_pct}%` : "—"}
                  </td>
                  <td className="px-2 py-1 text-center text-[10px]"
                       style={{ color: t.status === "OPEN" ? "var(--bull)" : "var(--text-muted-2)" }}>
                    {t.status}
                  </td>
                  <td className="px-2 py-1 text-center text-[10px] text-muted-2">
                    {t.exit_reason || (t.status === "OPEN" ? "—" : "")}
                  </td>
                </tr>
              ))}
              {!trades.length && (
                <tr>
                  <td colSpan={10} className="px-2 py-6 text-center text-muted-3">
                    No trades in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default PortfolioPage;
